import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SpedizioniService} from '../../../_services/spedizioni.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {Spedizione} from '../../../_models/Spedizione';
import {Risorsa} from '../../../_models/Risorsa';
import {SaveMessage} from '../../../_helpers/save-message';
import {constants} from '../../../_helpers/constants';
import {AuthenticationService} from '../../../_services/authentication.service';
import {Movimento} from '../../../_models/Movimento';
import {MessageConfirm} from '../../../_helpers/message-confirm.service';
import {MovimentiService} from '../../../_services/movimenti.service';
import {Subscription} from 'rxjs';
import {StatoEnum} from '../../../_models/generated/stato-enum';
import {ModalController} from '@ionic/angular';
import {DocumentoMagazzinoAddContenitoriComponent} from '../documento-magazzino-add-contenitori/documento-magazzino-add-contenitori.component';
import {DocumentoMagazzinoAddPrelievoComponent} from '../documento-magazzino-add-prelievo/documento-magazzino-add-prelievo.component';
import {Location} from '@angular/common';
import {RisorseService} from '../../../_services/risorse.service';
import {LocalService} from '../../../_services/local.service';
import {Ordine} from '../../../_models/Ordine';
import {OrdineGroup} from '../../../_models/OrdineGroup';
import {Contenitore} from '../../../_models/Contenitore';
import {CqSequenceOutcome} from '../../../_models/CqSequenceOutcome';
import {ControlliQualitaOrdineComponent} from '../../qualita/controlli-qualita-ordine/controlli-qualita-ordine.component';

// Feature 023: struttura di lavoro non persistente — un gruppo per terna
// (rifOdP, numFase, codLav) costruita dai box annidati della spedizione.
// I campi identificativi numerici (IdDocumento, IdRiga, IdRigaLavorazione) sono
// risolti SERVER-SIDE dal parsing di `rifOdP` (17 char compact) — vedi FR-004b.
interface GruppoCqSkeleton {
  rifOdP: string;
  numFase: number;
  codLav: string;
  faseContoLavoro: 1;
  movimentiInGruppo: Movimento[];
}

@Component({
  selector: 'app-documento-magazzino-detail',
  templateUrl: './documento-magazzino-detail.component.html',
  styleUrls: ['./documento-magazzino-detail.component.scss'],
  standalone: false
})
export class DocumentoMagazzinoDetailComponent implements OnInit, OnDestroy {
  idSpedizione = 0;
  spedizione: Spedizione;
  magazzino: Risorsa;
  loading = false;
  subscription: Subscription;
  protected readonly StatoEnum = StatoEnum;
  protected readonly constants = constants;
  // Feature 023 FR-010a: flag letto **una sola volta** in ngOnInit e cached come property
  // locale; modifiche del flag dalla schermata preferenze non sono retroattive sulla pagina
  // già aperta (richiede reload). Coerente con pattern altri Active*.
  activeCqRientroTerzista = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private saveMessage: SaveMessage,
    private deleteConfirm: MessageConfirm,
    public authService: AuthenticationService,
    private organizzazioneService: OrganizzazioniService,
    private movimentiService: MovimentiService,
    private spedizioniService: SpedizioniService,
    private risorseService: RisorseService,
    private localService: LocalService,
    private modalCtrl: ModalController) { }

  ngOnInit() {
    // Feature 023 FR-010a: lettura cached del flag, una sola volta al caricamento.
    this.activeCqRientroTerzista = this.organizzazioneService.activeCqRientroTerzista;
    this.route.params.subscribe(params => {
      this.idSpedizione = +params.idSpedizione;
      this.getSpedizione();
    });
    this.subscription = this.movimentiService.lastMovimentoCreated
      .subscribe((_movimento: Movimento) => {
        if (!this.spedizione) {
          return false;
        }
        if (!this.spedizione.movimenti) {
          this.spedizione.movimenti = [];
        }
        if (_movimento && _movimento.idSpedizione === this.idSpedizione) {
          this.getSpedizione();
        }
      });
    this.subscription.add(this.movimentiService.lastMovimentoUpdated
      .subscribe((movimento: Movimento) => {
        if (!this.spedizione) {
          return false;
        }
        if (!this.spedizione.movimenti || movimento.idSpedizione !== this.idSpedizione) {
          return false;
        }
      }));
    this.subscription.add(this.spedizioniService.lastSpedizioneUpdated.subscribe((_spedizione: Spedizione) => {
      if (_spedizione == null || this.spedizione == null ||
        _spedizione.idSpedizione !== this.spedizione.idSpedizione) {
        return;
      }
      this.spedizione.stato = _spedizione.stato;
    }));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getSpedizione() {
    this.loading = true;
    this.spedizioniService.getSpedizione(this.organizzazioneService.organizzazioneId,
      this.idSpedizione)
      .then((_spedizione: Spedizione) => this.spedizione = _spedizione)
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il caricamento della spedizione."))
      .finally(() => this.loading = false);
  }

  confirmSpedizione(){
    this.deleteConfirm.presentAlert(`Procedere con invio di ${this.spedizione.movimenti.length} articoli?`,() => this.sendMateriale());
  }

  sendMateriale = () => {
    this.loading = true;
    this.spedizione.dataDocumento = new Date();
    this.spedizione.stato = StatoEnum.INVIO_TERZISTA_WAIT;
    this.spedizioniService.updateSpedizione(this.spedizione)
      .then((_spedizione: Spedizione) => {
        this.saveMessage.messageInfo('Spedizione inviata.', true);
        this.router.navigate(['/movimenti/invia-materiale-terzista']);
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con l'invio."))
      .finally(() => this.loading = false);
  };

  removeMovimento = (movimento: Movimento) => {
    this.deleteConfirm
      .presentAlert(`Sei sicuro di voler eliminare movimento ${movimento.codArt}?`, () => {
        movimento.movimentiByBox.forEach((movimentoBox: Movimento) => {
          this.movimentiService.deleteMovimentoAsync(this.organizzazioneService.organizzazioneId, movimentoBox.id)
            .then(() => this.deleteMovimento(movimentoBox));
        });
      });
  };

  deleteMovimento = (movimento: Movimento) => {
    this.movimentiService.deleteMovimentoAsync(this.organizzazioneService.organizzazioneId, movimento.id)
      .then(() => this.spedizione.movimenti = this.spedizione.movimenti.filter((_mov: Movimento) => _mov !== movimento))
      .catch(() => this.saveMessage.messageError("Ci sono problemi con l'eliminazione del movimento."));
  };

  async openModal(giacenza?: Movimento) {
    const modal = await this.modalCtrl.create({
      component: DocumentoMagazzinoAddContenitoriComponent,
      componentProps: {
        idSpedizione: this.idSpedizione,
        idAnagrafica: this.spedizione.idAnagrafica,
        codPallet: giacenza?.parentBox ?? '',
        movimenti: giacenza?.movimentiByBox ?? []
      },
      backdropDismiss: false, cssClass: 'custom-modal'
    });
    await modal.present();
    const {role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.getSpedizione();
    }
  }

  async openPrelievo() {
    const modal = await this.modalCtrl.create({
      component: DocumentoMagazzinoAddPrelievoComponent,
      componentProps: {
        codArt: this.spedizione.ordineSpedizione.codArt,
        magazzino: this.spedizione.risorsa, movimenti: this.spedizione.movimenti,
        spedizione: this.spedizione
      },
      backdropDismiss: false, cssClass: 'custom-modal'
    });
    await modal.present();
    const {role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.getSpedizione();
    }
  }

  goBack = () => this.location.back();

  // ===== Controlli Qualità rientro da terzista (feature 023) =====
  // Trigger manuale (bottone) richiamabile dal dettaglio spedizione quando contesto = 2
  // (DDT da terzista) e flag utente `activeCqRientroTerzista` attivo. La logica è la
  // stessa originariamente eseguita in coda a EvasioneDdtTerzistiComponent.evadiRighe:
  // 1. raggruppamento movimenti per terna (idDocumento, idRiga, idRigaLavorazione) della
  //    FASE di produzione (recuperata dai contenitori via backend),
  // 2. chiamata batch CalcoloTempoOrdineGroup,
  // 3. overlay frontend dei contenitori per ordine,
  // 4. apertura ControlliQualitaOrdineComponent in modalità modaRientroTerzista.
  async openCqRientroTerzista() {
    if (!this.spedizione || this.spedizione.contesto !== 2) {
      return;
    }
    if (!this.spedizione.movimenti?.length) {
      this.saveMessage.messageError("Nessun movimento sulla spedizione: impossibile aprire i controlli qualità.");
      return;
    }
    this.loading = true;
    try {
      const gruppi = this.buildGruppiCqFromSpedizione(this.spedizione);
      if (gruppi.length === 0) {
        this.saveMessage.messageError("Impossibile aprire i controlli qualità: nessun contenitore con riferimento ordine produzione valorizzato.");
        return;
      }
      // Feature 023 FR-004 / FR-004b: skeleton minimale — solo rifOdP (17 char compact) +
      // numFase + codLav + FaseContoLavoro=1; IdDocumento/IdRiga/IdRigaLavorazione lasciati
      // a 0, il backend li risolve via ParseRifOdP server-side (no N+1 frontend).
      const orgId = this.organizzazioneService.organizzazioneId;
      const skeleton: OrdineGroup = {
        organizzazioneId: orgId,
        note: '',
        commessa: '',
        ordini: gruppi.map(g => ({
          organizzazioneId: orgId,
          idDocumento: 0,
          idRiga: 0,
          idRigaLavorazione: 0,
          rifOdP: g.rifOdP,
          numFase: g.numFase,
          codLav: g.codLav,
          faseContoLavoro: 1,
          note: '',
          commessa: ''
        } as unknown as Ordine))
      } as unknown as OrdineGroup;
      let arricchito: OrdineGroup;
      try {
        arricchito = await this.localService.getOrdiniGroupTempoAsync(skeleton);
      } catch (err: any) {
        const status = err?.status ?? 'unknown';
        const body = err?.error ? JSON.stringify(err.error) : (err?.message ?? '');
        console.error('[CQ-Rientro] CalcoloTempoOrdineGroup error:', status, body, err);
        this.saveMessage.messageError(`Impossibile caricare i controlli qualità (HTTP ${status}).`);
        return;
      }
      if (!arricchito?.ordini?.length) {
        this.saveMessage.messageError("La risposta di CalcoloTempoOrdineGroup non contiene ordini.");
        return;
      }
      arricchito.ordini.forEach((ord, i) => {
        const gruppo = gruppi[i];
        if (!gruppo) {
          return;
        }
        ord.contenitori = gruppo.movimentiInGruppo
          .map(m => this.mapMovimentoToContenitore(m))
          .filter(c => c != null);
      });
      const modal = await this.modalCtrl.create({
        component: ControlliQualitaOrdineComponent,
        backdropDismiss: false,
        componentProps: {
          ordine: arricchito,
          tipoControllo: 2,
          modaRientroTerzista: true
        },
        cssClass: 'custom-modal'
      });
      await modal.present();
      const {data, role} = await modal.onWillDismiss();
      this.handleCqOutcome(data as CqSequenceOutcome | undefined, role);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Feature 023 FR-025: itera la struttura ANNIDATA `spedizione.movimenti[].movimentiByBox[]`
   * (NON `spedizione.movimenti` flat — quello sono le righe DDT). Raggruppa i `box` per terna
   * (`rifOdP`, `numFase`, `codLav`). Box senza `rifOdP` sono esclusi. No N+1 frontend.
   */
  private buildGruppiCqFromSpedizione(spedizione: Spedizione): GruppoCqSkeleton[] {
    const gruppi: GruppoCqSkeleton[] = [];
    if (!spedizione?.movimenti?.length) {
      return gruppi;
    }
    const map = new Map<string, GruppoCqSkeleton>();
    for (const riga of spedizione.movimenti) {
      const boxes = riga.movimentiByBox ?? [];
      for (const box of boxes) {
        if (!box.rifOdP) {
          continue;
        }
        const numFase = box.numFase ?? 0;
        const codLav = box.codLav ?? '';
        const key = `${box.rifOdP}|${numFase}|${codLav}`;
        let g = map.get(key);
        if (!g) {
          const nuovo: GruppoCqSkeleton = {
            rifOdP: box.rifOdP,
            numFase: numFase,
            codLav: codLav,
            faseContoLavoro: 1,
            movimentiInGruppo: []
          };
          map.set(key, nuovo);
          gruppi.push(nuovo);
          g = nuovo;
        }
        g.movimentiInGruppo.push(box);
      }
    }
    gruppi.sort((a, b) => (a.rifOdP ?? '').localeCompare(b.rifOdP ?? '') || (a.numFase ?? 0) - (b.numFase ?? 0));
    return gruppi;
  }

  /**
   * Feature 023 FR-004b: parsing di `rifOdP` è server-side. Il `Contenitore` overlay riceve
   * `idDocumento: 0` come segnaposto; `rifOdP` resta come unica chiave funzionale.
   */
  private mapMovimentoToContenitore(m: Movimento): Contenitore {
    return {
      organizzazioneId: m.organizzazioneId,
      id: 0,
      codice: m.codBox ?? '',
      descrizione: '',
      isActive: true,
      formato: 0,
      tipoBox: m.tipoQuantita ?? 0,
      codArt: m.codArt,
      varianteArt: m.varianteArt,
      udM: m.unitaMisura,
      udM2: m.udM2,
      magUdM: m.magUdM,
      giacenza: m.quantita,
      giacenzaUdM2: m.quantitaUdM2,
      codLottoAlfa: m.codLottoAlfa,
      codMagazzino: m.codMagazzinoIn,
      codMagArea: m.codMagAreaIn,
      rifOdP: m.rifOdP,
      idDocumento: 0,
      idRiga: m.idRigaMateriale ?? 0,
      isNc: false
    };
  }

  private handleCqOutcome(data: CqSequenceOutcome | undefined, _role: string | undefined) {
    if (!data) {
      return;
    }
    switch (data.outcome) {
      case 'completata': {
        const rettif = data.movimentiRettificaCount;
        const dettagli = rettif ? ` (${rettif.conforme} conformi, ${rettif.nc} non conformi)` : '';
        this.saveMessage.messageInfo(`Controlli qualità completati: ${data.savedCount} ordini processati${dettagli}.`);
        this.getSpedizione();
        break;
      }
      case 'interrotta': {
        this.saveMessage.messageInfo(`Sequenza CQ interrotta — ${data.savedCount} gruppi su ${data.positionAtEnd} processati.`);
        this.getSpedizione();
        break;
      }
      case 'errore': {
        this.saveMessage.messageError(data.errorMessage ?? "Errore durante i controlli qualità.");
        break;
      }
    }
  }
}
